import React from 'react';
import usePermissions from '../../hooks/usePermissions';
import { SystemFunction, Permission } from '../../lib/permissions/constants';
import { Alert, Box } from '@mui/material';

interface PermissionGuardProps {
  require: SystemFunction;
  level?: Permission;
  fallback?: React.ReactNode;
  showMessage?: boolean;
  message?: string;
  children: React.ReactNode;
}

/**
 * PermissionGuard Component
 *
 * Conditionally renders children based on user permissions.
 *
 * @example
 * ```tsx
 * <PermissionGuard require={SystemFunction.EDIT_ORGANIZATION}>
 *   <Button>Save Changes</Button>
 * </PermissionGuard>
 * ```
 *
 * @example With message
 * ```tsx
 * <PermissionGuard
 *   require={SystemFunction.INVITE_USERS}
 *   showMessage={true}
 *   message="Only admins can invite users"
 * >
 *   <Button>Invite Member</Button>
 * </PermissionGuard>
 * ```
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  require,
  level = Permission.EDIT,
  fallback = null,
  showMessage = false,
  message,
  children,
}) => {
  const { can } = usePermissions();

  if (!can(require, level)) {
    if (showMessage) {
      return (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            {message || 'You do not have permission to access this feature'}
          </Alert>
        </Box>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionGuard;
